import React from 'react';
import { IonRouterOutlet, IonLoading } from '@ionic/react';
import {
  Route, Redirect, Switch, useHistory,
} from 'react-router-dom';
import ErrorScreen from 'pages/ErrorScreen';
import BootstrapLoadingScreen from 'pages/bootstrap/BootstrapLoadingScreen';
import UpdateUserApp from '../pages/version/UpdateUserApp';
import { registrationRoutes, registrationPaths } from '../routes/registrationRoutes';
import { useBootStrap, BOOTSTRAP_ERROR_STATES } from '../hooks/useBootstrap';
import useDelayedLoadingState from '../hooks/useDelayedLoadingState';

const ERROR_COMPONENTS = {
  [BOOTSTRAP_ERROR_STATES.VERSION_ERROR]: ErrorScreen,
  [BOOTSTRAP_ERROR_STATES.VERSION_INCOMPATIBLE]: UpdateUserApp,
  default: ErrorScreen,
};

export const ErrorHandler = ({ error, retryBootstrap }) => {
  const ErrorComponent = ERROR_COMPONENTS[error] || ERROR_COMPONENTS.default;
  console.error('AuthShell error:::', error);
  return <ErrorComponent onLogin={retryBootstrap} />;
};

const RegistrationStack = ({ isLoading }) => (
  <>
    <IonLoading isOpen={isLoading} message="Loading..." cssClass="ion-loading-custom" />
    <IonRouterOutlet>
      <Switch>
        {registrationRoutes.map((route) => (
          <Route exact path={route.path} component={route.component} key={route.path} />
        ))}
        <Redirect to={registrationPaths.login} />
      </Switch>
    </IonRouterOutlet>
  </>
);

const AuthShellContentRenderer = ({
  isBootstrapLoading,
  showLoadingScreen,
  isBootstrapped,
  children,
  isAuthLoading,
  error,
  retryBootstrap,
}) => {
  if (error) {
    return <ErrorHandler error={error} retryBootstrap={retryBootstrap} />;
  }
  if (isBootstrapLoading && !showLoadingScreen) {
    return null;
  }
  if (showLoadingScreen) {
    return <BootstrapLoadingScreen />;
  }
  if (isBootstrapped) {
    return children;
  }
  return <RegistrationStack isLoading={isAuthLoading} />;
};

const AuthShell = ({ children }) => {
  const {
    isBootstrapped,
    isLoading: isAuthLoading,
    error,
    landingRoute,
    isBootstrapLoading,
    retryBootstrap,
  } = useBootStrap();
  const history = useHistory();

  React.useEffect(() => {
    if (landingRoute) {
      history.replace(landingRoute);
    }
  }, [landingRoute]);

  const showBootstrapLoadingScreen = useDelayedLoadingState(isBootstrapLoading);
  const showAuthLoadingSpinner = useDelayedLoadingState(isAuthLoading);

  return (
    <AuthShellContentRenderer
      isBootstrapLoading={isBootstrapLoading}
      showLoadingScreen={showBootstrapLoadingScreen}
      isBootstrapped={isBootstrapped}
      isAuthLoading={showAuthLoadingSpinner}
      error={error}
      retryBootstrap={retryBootstrap}
    >
      {children}
    </AuthShellContentRenderer>
  );
};

export default AuthShell;
